import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../user/permissions';

import useAppContext from '../../../context/useAppContext';
import { ToastContext, WeezHeader, SplitContainer, SelectHierarchy, FrameCard, Input, Form, Button, ToggleLabel, MiniTab, Message } from '@weezevent/nacre';
import LoadingCard from '../../../components/loadingCard';
import { requiredField, useForm } from '../../../utils/validators';
import { getAvailableTypes, typesWithoutPreview } from '../list/components/helper';
import PreviewForm, { PreviewImage } from './components/previewForm';
import { splitTemplatesBaseCustom } from './templateForm';

import EventModel from '../../../models/eventModel';
import DesignModel from '../../../models/designModel';
import DesignTemplateModel, { templateTypes } from '../../../models/designTemplateModel';

import NoDesignFound from './empty/noDesignFound';
import EventsPicker from '../../../pages/charts/chart/inventory/EventsPicker';
import RatesPicker from './components/ratesPicker';

import RulesEmpty from '../list/components/design-row/rulesEmpty';
import MarketingPromotion from './components/marketingPromotion';
import { FormComponents } from './components/config-form/components/formComponents';

import Style from './designForm.module.css';
import { convertFieldsArrayToObject } from './configUtils';
import RatesTreeModel from '../../../models/ratesTreeModel';

const getDefaultConfig = (selectedTemplate, designConfig = null) => {
    //@Todo Remove if refacto format 'config_form_fields' permanent array
    const configFormTemplate = convertFieldsArrayToObject(selectedTemplate);

    let config = {};
    if (designConfig) config = designConfig;
    //add fields default_value in default config
    if (selectedTemplate && configFormTemplate) {
        const defaultConfig = Object.keys(configFormTemplate).reduce((fieldsForm, field) => {
            fieldsForm[field] = configFormTemplate[field]['default_value'];
            return fieldsForm;
        }, {});
        if (!Object.keys(config).includes('default')) config['default'] = {};
        Object.keys(defaultConfig).forEach(key => {
            if (!Object.keys(config['default']).includes(key) || config['default'][key] === null || config['default'][key] === '') config['default'][key] = defaultConfig[key];
        });
        return config;
    }
};

const GeneralInformation = React.memo(
    ({
        templates,
        form,
        setForm,
        formErrors,
        designId,
        event,
        organization,
        hasDefaultRule,
        isPremium,
        reloadPreviewOnFormChange,
        setPreviewSrc,
        setTemplateFields,
        forceCreateDefaultDesign
    }) => {
        const { t } = useTranslation();

        const [updated, isUpdated] = React.useState({ name: false, template: false });
        const [miniTabPane, setMiniTabPane] = React.useState('standard');

        const onFormChange = React.useCallback(
            (selected, id) => {
                if (id === 'name') isUpdated({ ...updated, name: true });
                if (id === 'templateid') {
                    isUpdated({ ...updated, template: true });
                    const selectedTemplate = templates.find(t => t.id === selected);
                    const config = getDefaultConfig(selectedTemplate?.config_form_fields);
                    if (selectedTemplate && config) {
                        setTemplateFields(convertFieldsArrayToObject(selectedTemplate.config_form_fields));
                        reloadPreviewOnFormChange({
                            code: selectedTemplate.code || {},
                            config: config,
                            is_download: false,
                            type: selectedTemplate.type || templateTypes.PDF_TICKET
                        });
                    } else {
                        setTemplateFields(null);
                        setPreviewSrc(null);
                    }
                    setForm('config', { ...config });
                }
                if (id === 'type') {
                    setForm('templateid', null);
                    setTemplateFields(null);
                    setPreviewSrc(null);
                }
                setForm(id, selected);
            },
            [setForm, templates, reloadPreviewOnFormChange]
        );

        const typeOptions = React.useMemo(() => getAvailableTypes(templates, t), [templates, t]);

        const templatesOptions = React.useMemo(() => splitTemplatesBaseCustom(templates, form, t), [templates, form, t]);

        const onToggleAll = React.useCallback(e => {
            setForm('rulesDefault', e.target.checked);
        }, []);

        return (
            <FrameCard title={t('ticket.ticket_design.create_template.information')}>
                {!isPremium && (
                    <>
                        <MiniTab
                            panes={[
                                { label: 'Standard', value: 'standard' },
                                { label: 'Premium', value: 'premium' }
                            ]}
                            activeTab={miniTabPane}
                            onTabSelected={tab => {
                                setMiniTabPane(tab.value);
                            }}
                        />
                        {miniTabPane === 'premium' && <MarketingPromotion organization={organization} />}
                    </>
                )}
                <Form.Field className={!isPremium ? Style['spacing-top'] : null}>
                    <Form.Label>{t('ticket.ticket_design.designs.name')}</Form.Label>
                    <Input
                        errors={Boolean(updated.name && formErrors['name'])}
                        placeholder={t('ticket.ticket_design.create_template.name_placeholder')}
                        value={form?.name || ''}
                        maxLength={60}
                        onChange={e => onFormChange(e.target.value, 'name')}
                    />
                    <Form.FieldErrors errors={[updated.name && t(formErrors['name'])]} />
                </Form.Field>
                {isPremium && (
                    <>
                        <Form.Field>
                            <Form.Label>{t('ticket.ticket_design.create_design.type')}</Form.Label>
                            <SelectHierarchy
                                widthIndent="10px"
                                disabled={Boolean(designId)}
                                errors={Boolean(formErrors['type'])}
                                placeholder={t('ticket.ticket_design.create_design.placeholder_type')}
                                options={typeOptions}
                                value={form.type || ''}
                                onChange={selected => onFormChange(selected, 'type')}
                                onClear={designId ? null : () => onFormChange(null, 'type')}
                            />
                        </Form.Field>
                        <Form.Field className={Style['search-hierarchy']}>
                            <Form.Label>{t('ticket.ticket_design.templates.total')}</Form.Label>
                            <SelectHierarchy
                                widthIndent="10px"
                                errors={Boolean(updated.template && formErrors['templateid'])}
                                disabled={Boolean(designId || !form.type)}
                                options={templatesOptions}
                                placeholder={t('ticket.ticket_design.create_design.placeholder_template')}
                                value={form.templateid || null}
                                onChange={selected => {
                                    onFormChange(selected, 'templateid');
                                }}
                                onClear={designId ? null : () => onFormChange(null, 'templateid')}
                            />
                            <Form.FieldErrors errors={[updated.template && t(formErrors['templateid'])]} />
                        </Form.Field>
                        {form?.type === 8 && (
                            <Message title={t('ticket.ticket_design.create_template.message.pvc_title')} type="default">
                                {t('ticket.ticket_design.create_template.message.pvc_description')}
                            </Message>
                        )}
                    </>
                )}
                <div className={Style['big-spacing-top']}>
                    <ToggleLabel
                        checked={hasDefaultRule}
                        inlinePosition="left"
                        label={t(`ticket.ticket_design.create_design.toggle_${event ? 'tickets' : 'events'}`)}
                        onChange={e => onToggleAll(e)}
                        disabled={forceCreateDefaultDesign}
                    />
                </div>
            </FrameCard>
        );
    }
);

const LinkRules = React.memo(({ organization, event, designId, prefix, items, itemsLoading, hasDefaultRule, form, setForm }) => {
    const { t } = useTranslation();
    const [querySearch, setQuerySearch] = React.useState('');
    //we get the form.rules (event or rate) data from the rules list (events or rates)
    const selectedRules = React.useMemo(() => {
        if (!form.rules) {
            return [];
        }
        return form.rules.map(formRule => items && (event ? formRule.rate_id : formRule.event_id));
    }, [event, form, items]);

    const onSelectRule = React.useCallback(
        selectedRulesList => {
            //we only store the id in the form.rules
            let rulesArray = selectedRulesList.map(rule => {
                if (rule.object) return event ? { rate_id: rule.object.id } : { event_id: rule.object.id };
                return event ? { rate_id: rule } : { event_id: rule };
            });
            setForm('rules', rulesArray);
        },
        [setForm, event, form]
    );

    const filteredEvents = React.useMemo(() => {
        if (event || !items) {
            return [];
        }
        return items
            .filter(i => i.publication_status !== 'closed' || selectedRules.includes(i.id))
            .sort((e1, e2) => new Date(e2.start_date).getTime() - new Date(e1.start_date).getTime());
    }, [event, items, selectedRules]);

    return (
        <div className={Style[`link-rules${hasDefaultRule ? '-hidden' : ''}`]}>
            <FrameCard
                title={t(`ticket.ticket_design.create_design.${event ? 'tickets' : 'events'}`)}
                subtitle={t(`ticket.ticket_design.create_design.${event ? 'tickets' : 'events'}_description`)}
            >
                <LoadingCard loading={itemsLoading}>
                    {!event ? (
                        <EventsPicker
                            label={t('ticket.ticket_design.create_design.events')}
                            organizationId={organization.id}
                            eventsDisplay
                            events={filteredEvents}
                            form={{ events: selectedRules }}
                            onChangeEvents={onSelectRule}
                            handleSearch={value => setQuerySearch(value)}
                            query={querySearch}
                            className={Style['event-picker']}
                        />
                    ) : (
                        <RatesPicker organization={organization} event={event} designId={designId} prefix={prefix} linkedRates={selectedRules} onChangeRules={onSelectRule} />
                    )}
                    {selectedRules?.length === 0 && !event && <RulesEmpty />}
                </LoadingCard>
            </FrameCard>
        </div>
    );
});

export default function DesignForm({ organization, designId = null, event = null }) {
    const { i18n, t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();
    const overwritePremium = React.useMemo(() => Boolean(permissions.hasCapacities(['PyvarCapacity.DESIGN_TEMPLATES', 'PyvarCapacity.DESIGN_ADMIN'], organization.id)), [
        permissions,
        organization
    ]);
    const toast = React.useContext(ToastContext);
    const [isNotFound, setIsNotFound] = React.useState(false);
    const [{ feature_flags }] = useAppContext();
    const isPremium = React.useMemo(() => feature_flags.includes('EO_GABARIT') || overwritePremium, [feature_flags, overwritePremium]);
    const [previewSrc, setPreviewSrc] = React.useState(null);
    const [loadingPreview, setLoadingPreview] = React.useState(false);
    const [ratesOptions, setRatesOptions] = React.useState([]);
    const [templateFields, setTemplateFields] = React.useState({});
    const [previewFormErrors, setPreviewFormErrors] = React.useState(null);
    const [forceCreateDefaultDesign, setForceCreateDefaultDesign] = React.useState(false);
    const [previewValid, setPreviewValid] = React.useState(true);

    const isReadOnly = React.useMemo(() => {
        if (organization.id !== 1 || permissions.hasCapacities(['PyvarCapacity.DEV'], organization.id)) {
            return false;
        }
        return true;
    }, [organization, permissions]);

    const isCanSubmit = React.useMemo(() => previewValid && !isReadOnly, [previewValid, isReadOnly]);

    const getStateLanguages = (config, fields) => {
        let languagesToSelect = [];
        if (!config) return languagesToSelect;
        Object.keys(config).forEach(lang => {
            let filledField = 0;
            Object.keys(config[lang]).forEach(field => {
                if (fields && fields[field] && fields[field]['params'] && fields[field]['params']['optional']) {
                    filledField++;
                    return;
                }
                let isEmpty = config[lang][field] == null || config[lang][field] === '';
                if (config[lang][field] && Object.keys(config[lang][field]).includes('value'))
                    isEmpty = config[lang][field]['value'] == null || config[lang][field]['value'] === '';
                if (!isEmpty && (lang === 'default' || config[lang][field] !== config['default'][field])) filledField++;
            });
            if (lang === 'default') {
                if (filledField === Object.keys(config[lang]).length) languagesToSelect.push(lang);
            } else if (filledField > 0) languagesToSelect.push(lang);
        });
        return languagesToSelect;
    };

    const validateConfig = React.useCallback(
        (name, config, form) => {
            const languagesToSelect = getStateLanguages(config, templateFields);
            return languagesToSelect.length > 0 && languagesToSelect.includes('default');
        },
        [templateFields]
    );

    const [formState, setFormState, setFormObject, formErrors, formValidate] = useForm(
        {
            name: '',
            templateid: null,
            type: templateTypes.PDF_TICKET,
            config: {},
            translations: {},
            rulesDefault: false, //we use a bool to store the state of defaultToggle to keep the rules array while editing
            rules: []
        },
        { name: requiredField, templateid: requiredField, config: validateConfig }
    );

    const [previewFormState, setPreviewFormState, setPreviewFormObject] = useForm(
        {
            event_id: null,
            rate_id: null,
            barcode: null,
            attendee_id: null,
            background: null,
            language: event?.default_language || organization?.default_language || 'fr-fr'
        },
        [organization, event]
    );

    const [, loadingDefaultDesigns] = DesignModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            defaultLoading: false,
            launch: Boolean(event?.id && organization.id && !designId),
            query: {
                only_default_event: true,
                limit: 1
            },
            onSuccess: response => {
                if (!response.length) {
                    setForceCreateDefaultDesign(true);
                    setFormState('rulesDefault', true);
                }
            }
        },
        [event, organization, designId]
    );

    React.useEffect(() => {
        formValidate();
    }, [formState]);

    const reloadPreview = React.useCallback(
        async (dataPreview, data = {}) => {
            let _previewValid = true;

            const mergedData = { ...dataPreview, ...data };
            if (typesWithoutPreview.includes(mergedData.type)) {
                return _previewValid;
            }
            setLoadingPreview(true);
            setPreviewFormErrors(null);
            await DesignModel.perform_preview({ data: mergedData }, { organization, event })
                .then(_src => {
                    setPreviewSrc(_src.response.preview);
                })
                .catch(_error => {
                    try {
                        if (_error && _error.response) {
                            const errorObject = JSON.parse(_error.response);
                            if (errorObject && errorObject['barcode']) setPreviewFormErrors(errorObject);
                        }
                    } catch {
                        console.error('JSON parse preview error');
                    }
                    toast.error(t('ticket.ticket_design.create_design.preview.error'));
                    setPreviewSrc(null);
                    _previewValid = false;
                })
                .finally(() => {
                    setLoadingPreview(false);
                    setPreviewValid(_previewValid);
                });
            return _previewValid;
        },
        [organization, event]
    );

    const getIsDefaultRule = React.useCallback(
        rules => {
            // if there isn't only one rule in formState.rules and no rule in design it's not default
            if (!rules || rules.length !== 1) return false;

            //we use either design.rules[0] when it's provided (onSuccess) or formState.rules[0]
            const rule = rules[0];

            //default for event level need the rule to have the same event_id and no rate_id
            //default for orga level need the rule to have no event_id nor rate_id
            return (event && rule.event_id === event.id && !rule.rate_id) || (!event && !rule.rate_id && !rule.event_id);
        },
        [event]
    );

    function simplifyRules(rulesToChange) {
        return rulesToChange.map(rule => {
            if (rule.rate_id) return { rate_id: rule.rate_id };
            if (rule.event_id) return { event_id: rule.event_id };
            return {};
        });
    }

    const hasDefaultRule = React.useMemo(() => {
        if (formState.rulesDefault) return true;
        // if there isn't only one rule in formState.rules and no rule in design it's not default
        return getIsDefaultRule(formState.rules);
    }, [formState, getIsDefaultRule]);

    const [templates, loadingTemplates] = DesignTemplateModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event ? event.pk : null,
            query: {
                include_shared_template: true,
                exclude_premiums: !feature_flags.includes('EO_GABARIT')
            },
            onSuccess: _templates => {
                if (!isPremium) {
                    const defaultTemplate = _templates.find(t => !t.is_premium && t.type === templateTypes.PDF_TICKET);
                    if (defaultTemplate) {
                        setFormState('templateid', defaultTemplate.id);
                        setTemplateFields(convertFieldsArrayToObject(defaultTemplate.config_form_fields));
                        const defaultConfig = getDefaultConfig(defaultTemplate.config_form_fields);
                        setFormState('config', defaultConfig);
                        reloadPreview({
                            code: defaultTemplate.code || {},
                            config: defaultConfig || { default: {} },
                            is_download: false,
                            type: templateTypes.PDF_TICKET,
                            event_id: null,
                            rate_id: null,
                            barcode: null,
                            attendee_id: null,
                            language: 'fr-fr'
                        });
                    }
                }
            }
        },
        [organization, isPremium, event, reloadPreview, feature_flags]
    );

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            query: {
                ordering: '-id'
            }
        },
        [organization]
    );

    const [, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: previewFormState.event_id,
            launch: Boolean(organization && previewFormState?.event_id),
            onSuccess: _rates => {
                setRatesOptions(_rates.toOptions());
            }
        },
        [organization, previewFormState]
    );

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/${event ? `E${event.id}/settings/` : ''}tickets_design/designs`;
    }, [organization, event]);

    const getClearConfig = React.useCallback(() => {
        const clearConfig = { ...formState.config };
        Object.keys(clearConfig).forEach(lang => {
            if (lang === 'default') return;
            Object.keys(clearConfig[lang]).forEach(field => {
                const value = clearConfig[lang][field];
                //delete default duplicates
                if (value === clearConfig['default'][field]) delete clearConfig[lang][field];
                //clear empty fields
                if (value === null || (typeof value === 'string' && value.trim().length === 0)) delete clearConfig[lang][field];
                //clear select_field empty values
                if (
                    value &&
                    typeof value === 'object' &&
                    !Array.isArray(value) &&
                    (clearConfig['default'][field]['value'] === value['value'] || value['value'] == null || value['value'] === '')
                )
                    delete clearConfig[lang][field];
                //to delete a language from the translations we need to send an empty object (locale-key: {})
            });
        });
        return clearConfig;
    }, [formState]);

    const selectedTemplate = React.useMemo(() => templates && templates.find(t => t.id === formState.templateid), [formState, templates]);

    const dataPreview = React.useMemo(
        () => ({
            code: selectedTemplate?.code || {},
            config: formState.config || { default: {} },
            is_download: false,
            type: formState.type || templateTypes.PDF_TICKET,
            language: previewFormState.language,
            event_id: previewFormState.event_id,
            rate_id: previewFormState.rate_id,
            barcode: previewFormState.barcode,
            attendee_id: null
        }),
        [formState, previewFormState, selectedTemplate]
    );

    const onSubmit = React.useCallback(() => {
        if (!formValidate() || !isCanSubmit) {
            if (Object.keys(formErrors).includes('config')) toast.error(t('ticket.ticket_design.create_design.default_config'));
            else toast.error(t('ticket.ticket_design.create_design.error'));
            return;
        }
        reloadPreview(dataPreview).then(response => {
            if (response) {
                //Clean and split config for save in translations
                let formConfig = getClearConfig();
                let defaultConfig = formConfig['default'];
                delete formConfig['default'];

                //default rules format: (event level) { event_id: %id% } / (orga level) { }
                const finalFormState = {
                    name: formState.name,
                    id: formState.id || null,
                    template_id: formState.templateid,
                    config: defaultConfig,
                    rules: formState.rulesDefault ? [event ? { event_id: event?.id } : {}] : formState.rules,
                    translations: { config: { ...formConfig } }
                };
                const toSaveDesign = new DesignModel(finalFormState);
                toSaveDesign
                    .save({ organizationId: organization.pk, eventId: event?.id })
                    .then(() => {
                        toast.success(t('ticket.ticket_design.create_design.success'));
                    })
                    .catch(() => {
                        toast.error(t('ticket.ticket_design.create_design.error'));
                    })
                    .finally(() => {
                        history.push(prefix);
                    });
            }
        });
    }, [formState, event, prefix, organization, formValidate, reloadPreview, dataPreview, previewValid, isCanSubmit]);

    const [labels, descriptions] = React.useMemo(() => {
        const lang = i18n.language.toLowerCase();
        const keys = ['labels', 'descriptions'];
        return keys.map(key => {
            const label_level = `config_form_${key}`;
            const trad = {};
            if (!selectedTemplate) return trad;
            // we check if there is a translation of the labels and descriptions otherwise we use the default ones
            Object.entries(selectedTemplate[label_level]).forEach(([field, defaultValue]) => {
                if (
                    selectedTemplate.translations &&
                    selectedTemplate.translations[label_level] &&
                    selectedTemplate.translations[label_level][lang] &&
                    selectedTemplate.translations[label_level][lang][field]
                ) {
                    trad[field] = selectedTemplate.translations[label_level][lang][field];
                } else trad[field] = defaultValue;
            });
            return trad;
        });
    }, [i18n, selectedTemplate]);

    const [, loadingDesign] = DesignModel.useApiModel(
        {
            organizationId: organization.id,
            id: designId,
            eventId: event?.id,
            launch: Boolean(designId && templates && organization),
            onSuccess: _design => {
                const templateId = _design.template_id;
                const selectedTemplate = templates.find(t => t.id === templateId);
                const templateType = selectedTemplate?.type;
                const configWithTranslations = _design.object.translations?.config
                    ? { ..._design.object.translations.config, default: _design.object.config }
                    : { default: _design.object.config };
                //merge default config with design saved config
                setTemplateFields(convertFieldsArrayToObject(selectedTemplate?.config_form_fields));
                const mergedConfig = getDefaultConfig(selectedTemplate?.config_form_fields, configWithTranslations);
                const isDefaultRules = getIsDefaultRule(_design.rules);
                setFormObject({
                    ..._design.object,
                    type: templateType,
                    templateid: templateId,
                    config: mergedConfig,
                    rulesDefault: isDefaultRules,
                    rules: isDefaultRules ? [] : simplifyRules(_design.rules)
                });
                reloadPreview({
                    code: selectedTemplate?.code || {},
                    config: mergedConfig || { default: {} },
                    is_download: false,
                    type: templateType,
                    event_id: null,
                    language: 'fr-fr',
                    rate_id: null,
                    barcode: null,
                    attendee_id: null,
                    background: null
                });
            },
            onError: () => {
                setIsNotFound(true);
            }
        },
        [organization, event, designId, templates, reloadPreview]
    );

    const isLoading = React.useMemo(() => (designId && loadingDesign) || loadingTemplates || loadingDefaultDesigns, [
        loadingDesign,
        loadingTemplates,
        designId,
        loadingDefaultDesigns
    ]);

    const selectedLanguages = React.useMemo(() => getStateLanguages(formState.config, templateFields), [formState, templateFields]);

    return (
        <div>
            <WeezHeader
                backLink={{ onClick: () => history.push(prefix) }}
                title={t(`ticket.ticket_design.create_design.${designId ? 'edit' : 'title'}`)}
                rightComponent={[
                    <Button key={0} disabled={!isCanSubmit || Object.keys(formErrors).filter(e => e !== 'config').length > 0} onClick={onSubmit} primary>
                        {t('ticket.common.action.save')}
                    </Button>
                ]}
            />
            <LoadingCard loading={isLoading}>
                {isNotFound ? (
                    <NoDesignFound organization={organization} event={event} />
                ) : (
                    <SplitContainer
                        className={Style['split-container']}
                        size="maxi"
                        additional={
                            <PreviewImage
                                disabled={!selectedTemplate}
                                previewSrc={previewSrc}
                                loadingPreview={loadingPreview}
                                reloadPreview={() => reloadPreview(dataPreview)}
                                templateType={selectedTemplate?.type}
                                previewValid={previewValid}
                            />
                        }
                    >
                        {event && !isPremium && hasDefaultRule && designId && (
                            <Message
                                title={t('ticket.ticket_design.designs.default.title')}
                                type="default"
                                className="external-links"
                                links={{
                                    label: (
                                        <a target="_blank" rel="noopener noreferrer" href={`/ticket/O${organization?.id}/E${event.id}/settings/tickets_design/designs/new`}>
                                            {t('ticket.ticket_design.designs.create')}
                                        </a>
                                    )
                                }}
                            >
                                {t('ticket.ticket_design.designs.default.create')}
                            </Message>
                        )}
                        <GeneralInformation
                            templates={templates}
                            form={formState}
                            setForm={setFormState}
                            formErrors={formErrors}
                            designId={designId}
                            organization={organization}
                            event={event}
                            hasDefaultRule={hasDefaultRule}
                            isPremium={isPremium}
                            reloadPreviewOnFormChange={data => reloadPreview(dataPreview, data)}
                            setPreviewSrc={setPreviewSrc}
                            setTemplateFields={setTemplateFields}
                            forceCreateDefaultDesign={forceCreateDefaultDesign}
                        />
                        {Boolean(selectedTemplate) && (
                            <>
                                <PreviewForm
                                    organization={organization}
                                    event={event}
                                    events={events}
                                    loadingRates={loadingRates}
                                    rates={ratesOptions}
                                    form={previewFormState}
                                    formErrors={previewFormErrors}
                                    setFormErrors={setPreviewFormErrors}
                                    setForm={setPreviewFormState}
                                    setFormObject={setPreviewFormObject}
                                    dataPreview={dataPreview}
                                    canDownload={selectedTemplate}
                                />
                                <FormComponents
                                    languages={event?.languages || organization.languages}
                                    selectedLanguages={selectedLanguages}
                                    labels={labels}
                                    descriptions={descriptions}
                                    fields={templateFields}
                                    config={formState.config}
                                    setConfig={config => {
                                        setFormState('config', config);
                                        setPreviewValid(true);
                                    }}
                                />
                            </>
                        )}
                        <LinkRules
                            prefix={prefix}
                            organization={organization}
                            event={event}
                            designId={designId}
                            items={event ? [] : events}
                            itemsLoading={event ? false : loadingEvents}
                            hasDefaultRule={hasDefaultRule}
                            form={formState}
                            setForm={setFormState}
                        />
                    </SplitContainer>
                )}
            </LoadingCard>
        </div>
    );
}
